import { useState } from "react";
import { X, Lock, CheckCircle } from "lucide-react";
import { changeAuthenticatedPassword } from "../services/authService";

/**
 * Modal to allow logged-in users to change their password.
 */
export default function ChangePasswordModal({ open, onClose }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  if (!open) return null;

  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setSuccess("");
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateInputs = () => {
    if (!currentPassword) return "Current password is required.";
    if (!newPassword) return "New password is required.";
    if (newPassword.length < 8) {
      return "New password must be at least 8 characters long.";
    }
    if (!/[A-Z]/.test(newPassword)) {
      return "New password must contain at least one uppercase letter.";
    }
    if (!/[0-9]/.test(newPassword)) {
      return "New password must contain at least one number.";
    }
    if (newPassword !== confirmPassword) {
      return "New password and Confirm password do not match.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const valErr = validateInputs();
    if (valErr) {
      setError(valErr);
      return;
    }

    setLoading(true);
    try {
      const result = await changeAuthenticatedPassword({
        currentPassword,
        newPassword,
        confirmPassword,
      });

      if (result.success) {
        setSuccess(result.message || "Password changed successfully.");
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setError(result.message || "Failed to change password.");
      }
    } catch (err) {
      setError(err.message || "An error occurred while changing your password.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-white border border-[#00000059] rounded-[8px] h-[50px] pl-4 pr-10 outline-none text-gray-800 text-[14px] placeholder:text-gray-400 disabled:opacity-60";
  const lblCls = "block text-[13px] font-semibold text-[#00000099] mb-1.5";

  return (
    <div
      onClick={loading ? undefined : handleClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0000001A] backdrop-blur-[14px]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[500px] mx-4 p-7 relative bg-[#E7F0F1] border border-[#00000033] backdrop-blur-[20px] shadow-[0px_0px_32px_0px_#3AA1A333] rounded-[20px]"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-[22px] font-bold text-gray-900 m-0 leading-tight">
              Change Password
            </h2>
            <p className="text-[13px] text-gray-400 mt-1">
              Update your account password.
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors border-none bg-transparent cursor-pointer disabled:opacity-50 text-gray-500 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Success / Error notification */}
        {error && (
          <div className="mb-5 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600 font-medium">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-5 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-600 font-medium flex items-center gap-2">
            <CheckCircle size={16} />
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 mb-6">
          {/* Current Password */}
          <div>
            <label className={lblCls}>Current Password</label>
            <div className="relative flex items-center">
              <input
                type={showCurrentPw ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                disabled={loading}
                className={inputCls}
              />
              <button
                type="button"
                onClick={() => setShowCurrentPw(!showCurrentPw)}
                className="absolute right-3 cursor-pointer opacity-70 hover:opacity-100 transition-opacity bg-transparent border-none outline-none"
              >
                <Lock size={16} />
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className={lblCls}>New Password</label>
            <div className="relative flex items-center">
              <input
                type={showNewPw ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 chars, 1 uppercase, 1 number"
                disabled={loading}
                className={inputCls}
              />
              <button
                type="button"
                onClick={() => setShowNewPw(!showNewPw)}
                className="absolute right-3 cursor-pointer opacity-70 hover:opacity-100 transition-opacity bg-transparent border-none outline-none"
              >
                <Lock size={16} />
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className={lblCls}>Confirm New Password</label>
            <div className="relative flex items-center">
              <input
                type={showConfirmPw ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={loading}
                className={inputCls}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPw(!showConfirmPw)}
                className="absolute right-3 cursor-pointer opacity-70 hover:opacity-100 transition-opacity bg-transparent border-none outline-none"
              >
                <Lock size={16} />
              </button>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end border-t border-gray-200/50 pt-5 mt-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="h-[40px] px-5 bg-[#FFFFFF] border border-[#0B617233] rounded-[8px] text-[13px] font-semibold text-[#5E5E5E] cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                className="h-[40px] px-5 bg-[#0B6172] border border-[#0B617233] rounded-[8px] text-[13px] font-semibold text-white cursor-pointer hover:bg-[#094f5e] transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  "Change Password"
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
